import { shellStringResources } from '../../Resources/ShellStringResources';
import { TaggableNamed } from '../base/taggable-named';
import { IModel } from '../base/annotation';
import { Metamodel } from '../base/model';
import { ICommitChange } from '../../interfaces/IResourceFilesProvider';
import { AdapterContext } from '../adapters/adapter-context';
import { IMutation, Mutation } from '../services/imutator';
import { IResourceBase } from '../../shared/utils/resourcefiles.utils';
import { Shadow } from '../services/shadow';
import { Owned } from '../base/named';
import { Folder } from '../folder/folder';
import { SharedModel } from '../sharedmodel';
import { EntityScope } from '../../shared/shared-constants';

export enum TemplateContributorType {
    custom = 'MyOrg',
    msft = 'Microsoft',
    community = 'Community'
}

export enum TemplateActivityFilter {
    PBISemanticModelRefresh = 'PBISemanticModelRefresh'
}

export interface ITemplateRequireLinkedService {
    supportTypes: string[];
    parameterSets?: Array<{
        types: string[];
        parameters: { [key: string]: string };
    }>;
    datasetTypeMap?: Array<{
        types: string[];
        datasets: { [key: string]: ITemplateDatasetTypeForLS };
    }>;
}

export interface ITemplateDatasetTypeForLS {
    type: string;
    copySourceType: string;
    copySinkType: string;
}

export interface ITemplateRequireSSISIR {
    type: 'AzureSSIS';
    parameters?: {
        starturl?: string[];
        stopurl?: string[];
        url?: string[];
    };
}

export interface ITemplateRequireBigDataPool {
    // To do: need to expand
    parameters?: {
        nodeCount?: number;
        nodeSize?: number;
    };
}

export interface ITemplateDescription {
    title: string;
    content: string;
}

export interface ITemplateRequires {
    linkedservices?: { [key: string]: ITemplateRequireLinkedService };
    integrationRuntimes?: { [key: string]: ITemplateRequireSSISIR };
    bigDataPools?: { [key: string]: ITemplateRequireBigDataPool };
}

export interface ITemplate {
    name: string;
    description: string | ITemplateDescription[];
    descriptionForPBI?: string | ITemplateDescription[];
    icons?: string[];
    image?: string;
    categories?: string[]; // a template may belong to multiple catogories
    relatedTemplates?: string[];
    requires?: ITemplateRequires;
    documentation?: string;
    author?: string;
    folder?: SharedModel.IFolder;
    tenant?: string[];
    annotations?: string[];
    services?: string[];
    scope?: EntityScope[];
    compatibleWithPBI?: boolean;
    contributorType?: TemplateContributorType; // a template may from Micorsoft, customers' orgs and community
}

export const templatesPath: string = 'templates';
export const manifestName: string = 'manifest';
export const templateNameRegex: RegExp = /^[\w\-. ]{1,140}$/;

const commitFileNamePath = (folderName: string, fileName: string) => folderName + '/' + fileName;

@Metamodel.model({
    name: 'template',
    display: [shellStringResources.ModelName_Template, shellStringResources.ModelName_Templates],
    reusable: true,
    referable: true,
    constraints: {
        name: {
            required: true,
            pattern: templateNameRegex,
            uniqueModelName: true
        }
    }
})
export class Template extends TaggableNamed {
    public static readonly MODEL: IModel;
    // in most case, displayName should be same with name, in rare case when name contains unsupported character or too long to be put into file name
    // the name in manifest can be different with the template name specified in folder name and file name.
    public displayName: string;
    public icons?: string[];
    public iconClasses?: string[];
    public custom: boolean;
    public payload?: string;
    public image?: string;
    public categories?: string[] = []; // a template may belong to multiple catogories
    public scope?: EntityScope[]; // the scope which a template applies to, 'ADF', 'Synapse', or 'Synapse Light'
    public compatibleWithPBI?: boolean = false; // indicates that the template can be used in trident
    public relatedTemplates?: string[];
    public requires?: ITemplateRequires;
    public documentation?: string;
    public author?: string;
    public userName?: string;
    public userImage?: string;
    public services?: string[] = [];
    public tenant: string[] = [];
    public contributorType?: TemplateContributorType;

    public folder: Folder = Owned.create(Folder.MODEL, this);

    public constructor(name: string, custom: boolean) {
        super(name);
        this.displayName = name;
        this.custom = custom;
    }

    public getChangesFromMutation(mutation: IMutation, adapterContext: AdapterContext): ICommitChange[] {
        if (!(mutation.named instanceof Template)) {
            return [];
        }
        const named = mutation.named as Template;
        const changes: ICommitChange[] = [];
        const templateResource: IResourceBase = {
            name: commitFileNamePath(named.name, named.name),
            type: templatesPath,
            folderName: named.folder ? named.folder.name : undefined
        };
        const manifestResource: IResourceBase = {
            name: commitFileNamePath(named.name, manifestName),
            type: templatesPath,
            folderName: named.folder ? named.folder.name : undefined
        };
        const adapter = adapterContext.adapterFor(Metamodel.modelFor(Template));
        let templatePayload: object = null;
        let manifestPayload: object = null;
        if (mutation.mutation === Mutation.ADD || mutation.mutation === Mutation.UPDATE) {
            templatePayload = named.payload ? JSON.parse(named.payload) : undefined;
            manifestPayload = adapter.toResource(named);
        }
        switch (mutation.mutation) {
            case Mutation.ADD:
                changes.push({
                    operation: 'add',
                    resource: manifestResource,
                    payload: manifestPayload
                });
                if (templatePayload) {
                    changes.push({
                        operation: 'add',
                        resource: templateResource,
                        payload: templatePayload
                    });
                }
                break;
            case Mutation.UPDATE:
                changes.push(
                    {
                        operation: 'update',
                        resource: manifestResource,
                        payload: manifestPayload
                    });
                if (templatePayload) {
                    changes.push({
                        operation: 'update',
                        resource: templateResource,
                        payload: templatePayload
                    });
                }
                break;
            case Mutation.DELETE:
                changes.push(
                    {
                        operation: 'delete',
                        resource: templateResource
                    }, {
                    operation: 'delete',
                    resource: manifestResource
                });
                break;
            case Mutation.RENAME:
                const shadow = Shadow.shadow(named);
                changes.push(
                    {
                        operation: 'rename',
                        resource: templateResource,
                        payload: { oldName: shadow ? commitFileNamePath(shadow.name, shadow.name) : undefined }
                    }, {
                    operation: 'rename',
                    resource: manifestResource,
                    payload: { oldName: shadow ? commitFileNamePath(shadow.name, manifestName) : undefined }
                });
                break;
            case Mutation.NOOP:
                break;
            default:
        }
        return changes;
    }
}
